import {useContext, useEffect, useState} from "react";
import AppContext from "../AppContext";
import {Button, Col, Form, Modal, Row} from "react-bootstrap";
import API, {HandleError} from "../API";
import {useSnackbar} from "notistack";
import {RiLockPasswordLine} from "react-icons/ri";
import {User} from "../models/User";
import {BsExclamationTriangle, BsHeartbreak} from "react-icons/bs";
import './AccountForm.scss'
import {useTranslation} from "react-i18next";

export default function AccountForm() {
    const app = useContext(AppContext)
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar()
    const [form, setForm] = useState({
        values: {
            first_name: '',
            last_name: '',
            old_password: '',
            new_password: ''
        },
        errorFields: {},
        error: false,
        loading: false,
    })

    const [deletionForm, setDeletionForm] = useState({
        values: {
            reason: 'price',
            reason_desc: '',
            agree: false
        },
        error: false,
        loading: false
    })

    const [showRequestDeletionModal, setShowRequestDeletionModal] = useState(false)
    const [showConfirmDeletionModal, setShowConfirmDeletionModal] = useState(false)

    const deletionReasons = {
        'product': t('deletionReasons.product'),
        'price': t('deletionReasons.price'),
        'competitor': t('deletionReasons.competitor'),
        'used': t('deletionReasons.used'),
        'other': t('deletionReasons.other')
    }

    useEffect(() => {
        setForm({...form, values: {...form.values,
            first_name: app.user?.first_name,
            last_name: app.user?.last_name,
        }})
    }, [])

    const onChange = (e) => {
        let values = {...form.values}
        values[e.target.name] = e.target.value
        setForm({...form, values: values})
    }

    const onDeletionChange = (e) => {
        let values = {...deletionForm.values}
        values[e.target.name] = e.target.name === 'agree' ? e.target.checked : e.target.value;
        setDeletionForm({...deletionForm, values: values})
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        app.startLoading()
        try {
            const res = await API({
                method: 'POST',
                url: `/account/${app.user?.ID}`,
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                },
                data: {...form.values}
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            app.stopLoading()
            setForm({...form, loading: false, values: {...form.values, old_password:'', new_password: ''}})
            app.setUser(new User(res.data.me))
            enqueueSnackbar(t('profile.updateSuccess'), {variant: 'success', style: {whiteSpace: 'pre-line'}});
        }
        catch(e) {
            app.stopLoading()
            HandleError(e, 'Error', enqueueSnackbar, app.logout);
        }
    }

    const openRequestDeletionModal = () => {
        setShowRequestDeletionModal(true)
    }

    const closeRequestDeletionModal = () => {
        setShowRequestDeletionModal(false)
    }

    const openConfirmDeletionModal = () => {
        setShowConfirmDeletionModal(true)
    }
    const closeConfirmDeletionModal = () => {
        setShowConfirmDeletionModal(false)
        closeRequestDeletionModal()
    }

    const requestDeletion = () => {

    }

    return <div style={{ maxWidth: 600}}>
        <Row>
            <Col md={6}>
                <Form.Group className="mb-3 form-floating" controlId="first_name">
                    <Form.Control type="text" placeholder={t('form.firstName')} name="first_name" value={form.values.first_name} onChange={onChange} />
                    <Form.Label>{t('form.firstName')}</Form.Label>
                    {'first_name' in form.errorFields ? <Form.Text className="text-muted">
                        {form.errorFields['first_name']}
                    </Form.Text> : ''}
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group className="mb-3 form-floating" controlId="last_name">
                    <Form.Control type="text" placeholder={t('form.lastName')} name="last_name" value={form.values.last_name} onChange={onChange} />
                    <Form.Label>{t('form.lastName')}</Form.Label>
                    {'last_name' in form.errorFields ? <Form.Text className="text-muted">
                        {form.errorFields['last_name']}
                    </Form.Text> : ''}
                </Form.Group>
            </Col>
            <Col md={12}>
                <Form.Group className="mb-3 form-floating" controlId="email" style={{border: 'none'}}>
                    <Form.Control type="email" placeholder={t('form.email')} name="email" style={{border: 'none'}} value={app.user?.email} readOnly={true} />
                    <Form.Label>{t('form.email')}</Form.Label>
                </Form.Group>
            </Col>
            <Col md={12} className="mt-4">
                <h5 className="mb-3"><RiLockPasswordLine /> {t('profile.changePassword')}</h5>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3 form-floating" controlId="old_password">
                            <Form.Control type="password" placeholder={t('form.oldPassword')} name="old_password" value={form.values.old_password} onChange={onChange} />
                            <Form.Label>{t('form.oldPassword')}</Form.Label>
                            {'old_password' in form.errorFields ? <Form.Text className="text-muted">
                                {form.errorFields['old_password']}
                            </Form.Text> : ''}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3 form-floating" controlId="new_password">
                            <Form.Control type="password" placeholder={t('form.newPassword')} name="new_password" value={form.values.new_password} onChange={onChange} />
                            <Form.Label>{t('form.newPassword')}</Form.Label>
                            {'new_password' in form.errorFields ? <Form.Text className="text-muted">
                                {form.errorFields['new_password']}
                            </Form.Text> : ''}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Button onClick={handleSubmit} color={"primary"} className="mt-4">
                            {t('button.saveChanges')}
                        </Button>
                    </Col>
                    <Col md={6}>
                        <Button onClick={openRequestDeletionModal} color={"danger"} variant="link" className="mt-4 text-danger fw-normal text-decoration-none">
                            {t('button.deleteAccount')}
                        </Button>
                    </Col>
                </Row>
            </Col>
        </Row>
        <Modal show={showRequestDeletionModal} onHide={closeRequestDeletionModal} className="account-deletion-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <BsHeartbreak />
                    <strong>{t('profile.delete.title')}</strong>
                    <span>{t('profile.delete.subtitle')}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-5 pt-5">
                <Form>
                    {Object.keys(deletionReasons).map((reason) =>
                        <div key={`del-reason-${reason}`} className="mb-3">
                            <Form.Check
                                name="reason"
                                type="radio"
                                id={`del-reason-${reason}`}
                                label={deletionReasons[reason]}
                                checked={deletionForm.values.reason === reason}
                                value={reason}
                                onChange={onDeletionChange}
                            />
                        </div>
                    )}
                    {deletionForm.values.reason === 'other' && <Form.Group className="mb-3" controlId="deletionReasonDesc">
                        <Form.Label>{t('profile.delete.reason')}</Form.Label>
                        <Form.Control as="textarea" rows={3} name="reason_desc" value={deletionForm.values.reason_desc} onChange={onDeletionChange} />
                    </Form.Group>}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={closeRequestDeletionModal}>
                    {t('profile.delete.cancel')}
                </Button>
                <Button variant="danger" onClick={openConfirmDeletionModal}>
                    {t('profile.delete.continue')}
                </Button>
            </Modal.Footer>
        </Modal>
        <Modal show={showConfirmDeletionModal} onHide={closeConfirmDeletionModal} className="account-deletion-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <BsExclamationTriangle />
                    <strong>{t('profile.deleteConfirm.title')}</strong>
                    <span>{t('profile.deleteConfirm.subtitle')}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-5 pt-5">
                <Form>
                    <h6 className="fw-bolder">{t('nb')}:</h6>
                    <p style={{fontSize: 14}}>
                        {t('profile.deleteConfirm.text1')}
                        <br />
                        <br />
                        {t('profile.deleteConfirm.text2')}
                    </p>
                    <div className="mt-5 mb-3" style={{fontSize: 14}}>
                        <Form.Check
                            name="agree"
                            type="checkbox"
                            id="delete-account-agreement"
                            label={t('profile.deleteConfirm.agree')}
                            checked={deletionForm.values.agree}
                            value={true}
                            onChange={onDeletionChange}
                        />
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeConfirmDeletionModal}>
                    {t('profile.deleteConfirm.cancel')}
                </Button>
                <Button variant="danger" onClick={requestDeletion}>
                    {t('profile.deleteConfirm.continue')}
                </Button>
            </Modal.Footer>
        </Modal>
    </div>
}